/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ContainerCreateRequest } from '../models/container-create-request';
import { ContainerCreateResponse } from '../models/container-create-response';
import { ContainerDetailedResponse } from '../models/container-detailed-response';
import { ContainerUpdateRequest } from '../models/container-update-request';
import { PageContainerAdminPageResponse } from '../models/page-container-admin-page-response';
import { PageContainerPageResponse } from '../models/page-container-page-response';
import { StatusUpdateRequest } from '../models/status-update-request';

/**
 * Container resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class ContainersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getContainer
   */
  static readonly GetContainerPath = '/containers/{containerId}';

  /**
   * Gets container.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContainer()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainer$Response(
    params: {
      containerId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContainerDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainersService.GetContainerPath,
      'get'
    );
    if (params) {
      rb.path('containerId', params.containerId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContainerDetailedResponse>;
        })
      );
  }

  /**
   * Gets container.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContainer$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainer(
    params: {
      containerId: string;
    },
    context?: HttpContext
  ): Observable<ContainerDetailedResponse> {
    return this.getContainer$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ContainerDetailedResponse>) =>
          r.body as ContainerDetailedResponse
      )
    );
  }

  /**
   * Path part for operation updateContainer
   */
  static readonly UpdateContainerPath = '/containers/{containerId}';

  /**
   * Updates a container.
   *
   * Updates an existing container based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateContainer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContainer$Response(
    params: {
      containerId: string;
      body: ContainerUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainersService.UpdateContainerPath,
      'put'
    );
    if (params) {
      rb.path('containerId', params.containerId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates a container.
   *
   * Updates an existing container based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateContainer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContainer(
    params: {
      containerId: string;
      body: ContainerUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateContainer$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateContainerStatus
   */
  static readonly UpdateContainerStatusPath = '/admin/containers/status';

  /**
   * Updates the status of containers by admin.
   *
   * Updates the status of containers based on the provided list of container ids and a status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateContainerStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContainerStatus$Response(
    params: {
      body: StatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainersService.UpdateContainerStatusPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates the status of containers by admin.
   *
   * Updates the status of containers based on the provided list of container ids and a status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateContainerStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContainerStatus(
    params: {
      body: StatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateContainerStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getContainers
   */
  static readonly GetContainersPath = '/containers';

  /**
   * Gets containers.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContainers()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainers$Response(
    params: {
      /**
       * Filter on publication status of products
       */
      status: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on ean
       */
      ean?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Filter on amount
       */
      amount?: number;

      /**
       * Filter on mainType of container
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of container
       */
      subType?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageContainerPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainersService.GetContainersPath,
      'get'
    );
    if (params) {
      rb.query('status', params.status, {});
      rb.query('brand', params.brand, {});
      rb.query('name', params.name, {});
      rb.query('ean', params.ean, {});
      rb.query('unit', params.unit, {});
      rb.query('amount', params.amount, {});
      rb.query('mainType', params.mainType, {});
      rb.query('subType', params.subType, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageContainerPageResponse>;
        })
      );
  }

  /**
   * Gets containers.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContainers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainers(
    params: {
      /**
       * Filter on publication status of products
       */
      status: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on ean
       */
      ean?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Filter on amount
       */
      amount?: number;

      /**
       * Filter on mainType of container
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of container
       */
      subType?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageContainerPageResponse> {
    return this.getContainers$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageContainerPageResponse>) =>
          r.body as PageContainerPageResponse
      )
    );
  }

  /**
   * Path part for operation createContainer
   */
  static readonly CreateContainerPath = '/containers';

  /**
   * Creates a new container.
   *
   * Creates a new container and returns the saved resource with its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createContainer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContainer$Response(
    params: {
      body: ContainerCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContainerCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainersService.CreateContainerPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContainerCreateResponse>;
        })
      );
  }

  /**
   * Creates a new container.
   *
   * Creates a new container and returns the saved resource with its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createContainer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContainer(
    params: {
      body: ContainerCreateRequest;
    },
    context?: HttpContext
  ): Observable<ContainerCreateResponse> {
    return this.createContainer$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ContainerCreateResponse>) =>
          r.body as ContainerCreateResponse
      )
    );
  }

  /**
   * Path part for operation getContainersForAdmin
   */
  static readonly GetContainersForAdminPath = '/admin/containers';

  /**
   * Gets containers for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContainersForAdmin()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainersForAdmin$Response(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on merchantName
       */
      merchantName?: string;

      /**
       * Filter on status
       */
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageContainerAdminPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainersService.GetContainersForAdminPath,
      'get'
    );
    if (params) {
      rb.query('name', params.name, {});
      rb.query('merchantName', params.merchantName, {});
      rb.query('status', params.status, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageContainerAdminPageResponse>;
        })
      );
  }

  /**
   * Gets containers for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContainersForAdmin$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainersForAdmin(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on merchantName
       */
      merchantName?: string;

      /**
       * Filter on status
       */
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageContainerAdminPageResponse> {
    return this.getContainersForAdmin$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageContainerAdminPageResponse>) =>
          r.body as PageContainerAdminPageResponse
      )
    );
  }
}
